const LOCK_KEY = 'moky_locale_locked';
const CHECKED_KEY = 'moky_locale_checked';
const CHECKED_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * @param {string} name
 * @returns {string | null}
 */
function readCookie(name) {
  const parts = `; ${document.cookie}`.split(`; ${name}=`);
  if (parts.length < 2) return null;
  return parts.pop()?.split(';').shift() || null;
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} maxAge
 */
function writeCookie(name, value, maxAge) {
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * @returns {boolean}
 */
function isLocked() {
  try {
    if (window.localStorage.getItem(LOCK_KEY) === '1') return true;
  } catch {
    // Storage can be blocked.
  }
  return readCookie(LOCK_KEY) === '1';
}

function lockPreference() {
  try {
    window.localStorage.setItem(LOCK_KEY, '1');
  } catch {
    // Storage can be blocked.
  }
  writeCookie(LOCK_KEY, '1', 60 * 60 * 24 * 365);
}

/**
 * @returns {boolean}
 */
function wasCheckedRecently() {
  try {
    const raw = window.sessionStorage.getItem(CHECKED_KEY);
    if (raw === '1') return true;
    const stamped = window.localStorage.getItem(CHECKED_KEY);
    if (!stamped) return false;
    const at = Number(stamped);
    return Number.isFinite(at) && Date.now() - at < CHECKED_MAX_AGE_MS;
  } catch {
    return readCookie(CHECKED_KEY) === '1';
  }
}

function markChecked() {
  try {
    window.sessionStorage.setItem(CHECKED_KEY, '1');
    window.localStorage.setItem(CHECKED_KEY, String(Date.now()));
  } catch {
    // Storage can be blocked.
  }
  writeCookie(CHECKED_KEY, '1', 60 * 60 * 24);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace('_', '-');
}

/**
 * @param {string[]} available
 * @param {string} value
 * @returns {string | null}
 */
function matchAvailable(available, value) {
  const needle = normalizeCode(value);
  if (!needle) return null;
  const exact = available.find((item) => normalizeCode(item) === needle);
  if (exact) return exact;
  const base = needle.split('-')[0];
  return available.find((item) => normalizeCode(item) === base || normalizeCode(item).split('-')[0] === base) || null;
}

/**
 * @param {string[]} availableLanguages
 * @returns {string | null}
 */
function languageFromBrowser(availableLanguages) {
  const tags = [...(navigator.languages || []), navigator.language].filter(Boolean);
  for (const tag of tags) {
    const match = matchAvailable(availableLanguages, tag);
    if (match) return match;
  }
  return null;
}

/**
 * @param {string[]} availableCountries
 * @returns {string | null}
 */
function countryFromBrowser(availableCountries) {
  const tags = [...(navigator.languages || []), navigator.language].filter(Boolean);
  for (const tag of tags) {
    const region = String(tag).split(/[-_]/)[1];
    if (!region) continue;
    const match = matchAvailable(availableCountries, region);
    if (match) return match;
  }
  return null;
}

/**
 * @param {unknown} payload
 * @returns {string | null}
 */
function countryFromSuggestions(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const detected = /** @type {{ detected_values?: { country?: { handle?: string } } }} */ (payload).detected_values;
  return detected?.country?.handle || null;
}

/**
 * @returns {Promise<string | null>}
 */
async function countryFromIp() {
  const root = window.Shopify?.routes?.root || '/';
  const url = `${root}browsing_context_suggestions.json`;
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;
  return countryFromSuggestions(await response.json());
}

/**
 * @param {HTMLFormElement} form
 */
function isLocalizationForm(form) {
  return Boolean(form.querySelector('[name="country_code"]') && form.querySelector('[name="language_code"]'));
}

function watchManualChanges() {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id === 'AutoLocalizationForm') return;
      if (!isLocalizationForm(form)) return;
      lockPreference();
    },
    true
  );
}

async function applyDetectedLocale() {
  if (window.Shopify?.designMode) return;
  if (isLocked() || wasCheckedRecently()) return;

  const form = document.getElementById('AutoLocalizationForm');
  const configEl = document.getElementById('AutoLocalizationConfig');
  if (!(form instanceof HTMLFormElement) || !(configEl instanceof HTMLScriptElement)) return;

  const countryInput = form.querySelector('[name="country_code"]');
  const languageInput = form.querySelector('[name="language_code"]');
  if (!(countryInput instanceof HTMLInputElement) || !(languageInput instanceof HTMLInputElement)) return;

  /** @type {{ countries?: string[]; languages?: string[] }} */
  let config = {};
  try {
    config = JSON.parse(configEl.textContent || '{}');
  } catch {
    config = {};
  }

  const availableCountries = Array.isArray(config.countries) ? config.countries : [];
  const availableLanguages = Array.isArray(config.languages) ? config.languages : [];

  if (availableCountries.length < 2 && availableLanguages.length < 2) return;

  const currentCountry = countryInput.value;
  const currentLanguage = languageInput.value;

  let detectedCountry = null;
  try {
    detectedCountry = await countryFromIp();
  } catch {
    detectedCountry = null;
  }

  const nextCountry =
    matchAvailable(availableCountries, detectedCountry || '') ||
    countryFromBrowser(availableCountries) ||
    currentCountry;
  const nextLanguage = languageFromBrowser(availableLanguages) || currentLanguage;

  const countryChanged = normalizeCode(nextCountry) !== normalizeCode(currentCountry);
  const languageChanged = normalizeCode(nextLanguage) !== normalizeCode(currentLanguage);

  markChecked();

  if (!countryChanged && !languageChanged) return;

  countryInput.value = nextCountry;
  languageInput.value = nextLanguage;
  form.submit();
}

watchManualChanges();
applyDetectedLocale();
